import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

// Import the same components used in the editor
import Hero from './PuckEditor/components/Hero';
import TextBlock from './PuckEditor/components/TextBlock';
import ImageBlock from './PuckEditor/components/ImageBlock';
import VideoBlock from './PuckEditor/components/VideoBlock';
import FeatureList from './PuckEditor/components/FeatureList';
import ContactForm from './PuckEditor/components/ContactForm';

// Create a fallback component for error handling
const FallbackComponent = ({ componentName }) => (
  <Box 
    sx={{ 
      p: 4, 
      border: '2px dashed #ccc', 
      borderRadius: 2, 
      textAlign: 'center',
      bgcolor: '#f9f9f9',
      my: 2
    }}
  >
    <Typography color="error" variant="h6">
      Component Error: {componentName}
    </Typography>
    <Typography variant="body2">
      This component failed to render.
    </Typography>
  </Box>
);

// HTML content renderer component
const HtmlRenderer = ({ html }) => {
  return (
    <Box sx={{ width: '100%' }}>
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </Box>
  );
};

/**
 * PuckRenderer - Renders Puck content or HTML content in read-only mode for public-facing pages
 * 
 * @param {Object} props
 * @param {Object} props.content - The Puck content or HTML content to render
 * @param {string} props.accountId - The account ID
 * @param {string} props.microSiteId - The microsite ID
 * @param {string} props.pageId - The page ID
 */
const PuckRenderer = ({ content, accountId, microSiteId, pageId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parsedContent, setParsedContent] = useState(null);
  const [isHtmlContent, setIsHtmlContent] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');

  useEffect(() => {
    try {
      // Check if this is HTML content format
      if (content && (content.isHtmlContent || content.contentType === 'html')) {
        setIsHtmlContent(true);
        setHtmlContent(content.html || content.content || '');
        setLoading(false);
        return;
      }
      
      // Parse content if it's a string
      if (typeof content === 'string') {
        try {
          setParsedContent(JSON.parse(content));
        } catch (e) {
          console.error('Error parsing content:', e);
          
          // Check if it looks like HTML
          if (content.includes('<html>') || content.includes('<body>') || content.includes('<div>')) {
            setIsHtmlContent(true);
            setHtmlContent(content);
          } else {
            setError('Invalid content format');
          }
        }
      } else {
        setParsedContent(content);
      }
    } catch (err) {
      console.error('Error processing content:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [content]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, border: '1px solid #f44336', borderRadius: 2, bgcolor: '#ffebee' }}>
        <Typography color="error" variant="h6">Error loading content</Typography>
        <Typography variant="body2">{error}</Typography>
      </Box>
    );
  }

  // If it's HTML content, use the HTML renderer
  if (isHtmlContent) {
    return <HtmlRenderer html={htmlContent} />;
  }
  
  // If content is missing or empty, show a message
  if (!parsedContent || !parsedContent.content) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6">No content available</Typography>
      </Box>
    );
  }

  // Get the background color from the root props
  const backgroundColor = 
    parsedContent.root?.props?.style?.backgroundColor || '#ffffff';

  // Render each component in the content array
  return (
    <Box sx={{ backgroundColor }}>
      {parsedContent.content.map((item, index) => {
        const { type, props } = item;
        
        // Render the appropriate component based on type
        switch (type) {
          case 'Hero':
            return (
              <Box 
                key={`component-${index}`}
                data-analytics-id={`hero-${index}`}
                data-analytics-type="Hero"
              >
                {renderComponent(Hero, props, 'Hero', index)}
              </Box>
            );
          case 'TextBlock':
            return (
              <Box 
                key={`component-${index}`}
                data-analytics-id={`text-block-${index}`}
                data-analytics-type="TextBlock"
              >
                {renderComponent(TextBlock, props, 'TextBlock', index)}
              </Box>
            );
          case 'ImageBlock':
            return (
              <Box 
                key={`component-${index}`}
                data-analytics-id={`image-block-${index}`}
                data-analytics-type="ImageBlock"
              >
                {renderComponent(ImageBlock, props, 'ImageBlock', index)}
              </Box>
            );
          case 'VideoBlock':
            return (
              <Box 
                key={`component-${index}`}
                data-analytics-id={`video-block-${index}`}
                data-analytics-type="VideoBlock"
              >
                {renderComponent(VideoBlock, props, 'VideoBlock', index)}
              </Box>
            );
          case 'FeatureList':
            return (
              <Box 
                key={`component-${index}`}
                data-analytics-id={`feature-list-${index}`}
                data-analytics-type="FeatureList"
              >
                {renderComponent(FeatureList, props, 'FeatureList', index)}
              </Box>
            );
          case 'ContactForm':
            return (
              <Box 
                key={`component-${index}`}
                data-analytics-id={`contact-form-${index}`}
                data-analytics-type="ContactForm"
              >
                {renderComponent(
                  ContactForm, 
                  { ...props, accountId, microSiteId, pageId }, 
                  'ContactForm', 
                  index
                )}
              </Box>
            );
          default:
            console.warn(`Unknown component type: ${type}`);
            return (
              <Box 
                key={`component-${index}`}
                data-analytics-id={`unknown-component-${index}`}
                data-analytics-type={type || 'Unknown'}
              >
                <FallbackComponent componentName={type || 'Unknown'} />
              </Box>
            );
        }
      })}
    </Box>
  );
};

// Helper function to safely render a component with error handling
function renderComponent(Component, props, name, index) {
  try {
    return <Component {...props} key={`${name}-${index}`} />;
  } catch (err) {
    console.error(`Error rendering ${name}:`, err);
    return <FallbackComponent componentName={name} />;
  }
}

export default PuckRenderer; 